'use client';

import { createContext, useContext } from 'react';

export type PerspectiveCategory = 'public' | 'insider' | 'mainScenario'

// Each tab in a :::perspectiveBox is keyed by a normalized category derived
// from its H2 title. Tabs are synchronized across boxes by category, not by
// index, so boxes with different tab sets (e.g. the epilogue boxes have only
// Public) still respect the global selection.
export const getPerspectiveCategoryFromTitle = (title: string): PerspectiveCategory => {
  const normalized = title.toLowerCase().trim();
  if (normalized.startsWith('public')) return 'public';
  if (normalized.startsWith('insider')) return 'insider';
  if (
    normalized.startsWith('main scenario')
    || normalized.startsWith('scenario')
  ) {
    return 'mainScenario';
  }
  return 'mainScenario';
};

// Centralized display labels for each perspective category. All
// PerspectiveBoxN variants share these so that renaming a tab (e.g.
// "Scenario" -> "Main Scenario") happens in exactly one place.
export const PERSPECTIVE_TAB_LABELS: Record<PerspectiveCategory, string> = {
  mainScenario: 'Main Scenario',
  public: "Public's Perspective",
  insider: "Insider's Perspective",
};

// Compact labels for tight spots (e.g. the feedback thread cards) where the
// full "...'s Perspective" phrasing is too long.
export const PERSPECTIVE_SHORT_LABELS: Record<PerspectiveCategory, string> = {
  mainScenario: 'Main Scenario',
  public: 'Public',
  insider: 'Insider',
};

export const PERSPECTIVE_TAB_DESCRIPTIONS: Record<PerspectiveCategory, string> = {
  mainScenario: 'How the policies get made',
  public: "Simpler and shorter",
  insider: "Gritty technical details",
};

const PERSPECTIVE_TAB_TOOLTIP_FOOTER = ""
{/* <div className="font-sans text-sm mt-3 text-black/50">(Use arrow keys to switch perspectives)</div>; */}

export const PERSPECTIVE_TAB_TOOLTIPS: Record<PerspectiveCategory, React.ReactNode> = {
  mainScenario: <div><b>Our default story,</b> for people interested in the details how the US and China would achieve stable, careful AI takeoff.
        {PERSPECTIVE_TAB_TOOLTIP_FOOTER}</div>,
  public: <div><b>A shorter, more accessible story,</b> from the viewpoint of an average white-collar worker.
        {PERSPECTIVE_TAB_TOOLTIP_FOOTER}</div>,
  insider: <div><b>A more technical story,</b> from the viewpoint of a frontier AI  researcher.
        {PERSPECTIVE_TAB_TOOLTIP_FOOTER}</div>,
};

// Perspective is a page-level mode: the whole page renders one perspective at a
// time, chosen from the end-of-story timeline diagram (and synced to the
// `?perspective=` URL param). Switching simply re-renders every :::perspectiveBox
// to the new tab — there's no mid-stream animation or scroll-anchoring anymore,
// so the context carries nothing but the selection and its setter.
export type PerspectiveTabContextValue = {
  selectedPerspective: PerspectiveCategory | null;
  setSelectedPerspective: (value: PerspectiveCategory | null) => void;
};

export const PerspectiveTabContext = createContext<PerspectiveTabContextValue | null>(null);

// Falls back to local component-level state semantics if no provider is
// mounted (e.g. when PerspectiveBox2 is used outside the playbook). The
// fallback is intentionally a no-op setter on a `null` value so callers can
// still read/write without crashing; PerspectiveBox2 supplements this with
// its own internal state when context is absent.
export const usePerspectiveTabContext = (): PerspectiveTabContextValue | null => {
  return useContext(PerspectiveTabContext);
};
